/**
 * Hébergement — Immersive hero + dates/guests picker + editorial rooms listing.
 *
 * Parcours :
 *   1. Hero image plein écran + horizontal card (check-in, check-out, adults, CTA)
 *   2. Auto-scroll vers la grille des catégories (Chambre Exécutive / Suite Jardin / Suite Lagune)
 *   3. Clic "Découvrir" → /hebergement/:slug (fiche détaillée)
 *   4. Depuis la fiche : "Réserver cette chambre" → tunnel booking avec dates + adultes pré-remplis
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { ArrowRight, CalendarDays, Users } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";
import HeroBackground from "../../components/vitrine/HeroBackground";

const HERO_IMAGE_FALLBACK =
  "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=2400&q=85";

// Fallback catégories utilisées si la section CMS `hebergement_fiches` est
// vide (bootstrap ou hors-ligne). En prod, la liste vient de site_settings.
const ROOMS_FALLBACK = [
  { slug: "chambre-executive", tierId: "superieure",  name: "Chambre Exécutive", tagline: "L'essentiel avec élégance", short: "", image: "" },
  { slug: "suite-jardin",      tierId: "suite_jardin", name: "Suite Jardin",      tagline: "Nature intime & sérénité", short: "", image: "" },
  { slug: "suite-lagune",      tierId: "suite_lagune", name: "Suite Lagune",      tagline: "Face à la lagune, sans filtre", short: "", image: "" },
];

export default function UniversHebergement() {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const tomorrow = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; }, []);

  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const listRef = useRef(null);

  const canContinue = !!checkIn && !!checkOut && checkOut > checkIn && adults >= 1;

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.max(0, Math.round((checkOut.getTime() - checkIn.getTime()) / 86400000));
  }, [checkIn, checkOut]);

  useEffect(() => {
    if (!confirmed || !listRef.current) return;
    const t = setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [confirmed]);

  function handleContinue() {
    if (!canContinue) return;
    trackEvent("hebergement_search", {
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
      adults,
      children,
    });
    setConfirmed(true);
  }

  const params = useMemo(() => {
    if (!checkIn || !checkOut) return "";
    const qs = new URLSearchParams({
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
      adults: String(adults),
    });
    if (children > 0) qs.set("children", String(children));
    return `?${qs.toString()}`;
  }, [checkIn, checkOut, adults, children]);

  return (
    <HebergementInner
      checkIn={checkIn}
      setCheckIn={setCheckIn}
      checkOut={checkOut}
      setCheckOut={setCheckOut}
      adults={adults}
      setAdults={setAdults}
      childrenCount={children}
      setChildren={setChildren}
      today={today}
      tomorrow={tomorrow}
      nights={nights}
      confirmed={confirmed}
      canContinue={canContinue}
      handleContinue={handleContinue}
      listRef={listRef}
      params={params}
    />
  );
}

function HebergementInner({ checkIn, setCheckIn, checkOut, setCheckOut, adults, setAdults, childrenCount, setChildren, today, tomorrow, nights, confirmed, canContinue, handleContinue, listRef, params }) {
  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_hebergement");
  const fiches = sel.hebergementFiches(cfg);
  // Fiches (chambre + suites) chargées depuis site_settings.hebergement_fiches.
  // Chaque item est mappé vers la structure attendue par le rendu de cette page.
  const ROOMS = useMemo(() => {
    const list = fiches?.rooms;
    if (!Array.isArray(list) || list.length === 0) return ROOMS_FALLBACK;
    return list.map((r) => ({
      slug: r.slug,
      tierId: r.tier_id,
      name: r.name,
      tagline: r.tagline || "",
      short: r.short || r.description || "",
      image: r.card_image || (Array.isArray(r.images) && r.images[0]) || "",
    }));
  }, [fiches]);
  const heroImage = ph.image_url || HERO_IMAGE_FALLBACK;
  const kicker = ph.kicker || "· Hébergement ·";
  const title = ph.title || "Composez votre séjour";
  const subtitle = ph.subtitle_long || "Sélectionnez vos dates d'arrivée et de départ, précisez le nombre de voyageurs et découvrez les hébergements disponibles : Chambre Exécutive intimiste, Suite Jardin lumineuse ou Suite Lagune face à l'eau.";
  const footerNote = ph.footer_note || "Chambre Exécutive · Suite Jardin · Suite Lagune";
  const videoUrl = ph.video_url || "";
  const children = childrenCount;
  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-hebergement">
      {/* ─── Hero immersif + booking card (CMS-driven iter-63/64) ─── */}
      <section className="relative w-full min-h-[100vh] overflow-hidden bg-[#0A0A0A]">
        <HeroBackground videoUrl={videoUrl} imageUrl={heroImage} />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/40" />

        <div className="relative z-10 min-h-[100vh] max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-28 flex flex-col items-center justify-center gap-8 md:gap-10">
          {/* MOBILE title */}
          <div className="md:hidden text-center text-white">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-white/85 mb-4">
              {kicker}
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] whitespace-pre-line">
              {title}
            </h1>
          </div>

          {/* Booking card — mobile: stacked · desktop: horizontal 2 columns */}
          <div
            className="w-full max-w-md md:max-w-5xl bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] md:grid md:grid-cols-2"
            data-testid="hebergement-booking-card"
          >
            {/* LEFT — booking inputs */}
            <div className="p-6 md:p-10 lg:p-12 md:border-r md:border-[#0A0A0A]/8 flex flex-col justify-center gap-5">
              <DateField
                label="Arrivée"
                value={checkIn}
                onChange={(d) => {
                  setCheckIn(d);
                  if (d && (!checkOut || checkOut <= d)) {
                    const co = new Date(d); co.setDate(co.getDate() + 1);
                    setCheckOut(co);
                  }
                }}
                minDate={today}
                testId="hebergement-checkin"
              />
              <DateField
                label="Départ"
                value={checkOut}
                onChange={setCheckOut}
                minDate={checkIn ? new Date(checkIn.getTime() + 86400000) : tomorrow}
                testId="hebergement-checkout"
              />
              <div className="grid grid-cols-2 gap-4">
                <CounterField
                  label="Adultes"
                  value={adults}
                  onChange={setAdults}
                  min={1}
                  max={20}
                  testId="hebergement-adults"
                />
                <CounterField
                  label="Enfants"
                  value={children}
                  onChange={setChildren}
                  min={0}
                  max={10}
                  testId="hebergement-children"
                />
              </div>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className="mt-3 w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#B8922A] text-white py-4 text-[0.7rem] tracking-[0.35em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="hebergement-continue"
              >
                Continuer
                <ArrowRight size={13} strokeWidth={1.5} />
              </button>
              {nights > 0 && (
                <div className="text-center text-[0.65rem] tracking-[0.25em] uppercase text-[#0A0A0A]/50">
                  {nights} nuit{nights > 1 ? "s" : ""} · {adults} adulte{adults > 1 ? "s" : ""}
                  {children > 0 && ` · ${children} enfant${children > 1 ? "s" : ""}`}
                </div>
              )}
            </div>

            {/* RIGHT — Context (desktop only) */}
            <div className="hidden md:flex p-10 lg:p-14 flex-col justify-center text-[#0A0A0A]">
              <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
                {kicker}
              </div>
              <h1 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] mb-8 whitespace-pre-line">
                {title}
              </h1>
              <p className="text-sm lg:text-base leading-[1.85] text-[#0A0A0A]/70 font-light">
                {subtitle}
              </p>
              {footerNote && (
                <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">
                  {footerNote}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Editorial listing — revealed after Continuer ─── */}
      {confirmed && (
        <section
          ref={listRef}
          className="py-20 md:py-28 bg-white scroll-mt-24"
          data-testid="hebergement-listing-section"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14 md:mb-20">
              <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-4">
                · Étape 2 · Disponibles pour votre séjour ·
              </div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                Nos hébergements
              </h2>
              {checkIn && checkOut && (
                <p className="text-sm text-[#0A0A0A]/55 mt-4">
                  Du {format(checkIn, "d MMM", { locale: frLocale })} au {format(checkOut, "d MMM yyyy", { locale: frLocale })} · {adults} adulte{adults > 1 ? "s" : ""}
                  {children > 0 && ` · ${children} enfant${children > 1 ? "s" : ""}`}
                </p>
              )}
            </div>

            {/* MOBILE — visual cards : image + centered title raised */}
            <div className="md:hidden space-y-8" data-testid="hebergement-list-mobile">
              {ROOMS.map((r) => (
                <Link
                  key={r.slug}
                  to={`/hebergement/${r.slug}${params}`}
                  className="block"
                  data-testid={`hebergement-mobile-${r.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#0A0A0A]">
                    <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/35" />
                    {/* Title raised to upper-center for stronger presence */}
                    <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-6 pt-2">
                      <div className="text-[0.55rem] tracking-[0.5em] uppercase text-white/70 mb-3">
                        · {r.tagline} ·
                      </div>
                      <h3 className="font-serif font-light text-4xl leading-[1.02] text-center">
                        {r.name}
                      </h3>
                    </div>
                  </div>
                  <div className="pt-5 text-center">
                    <span className="inline-block text-[0.7rem] tracking-[0.4em] uppercase text-[#0A0A0A] pb-1 border-b border-[#0A0A0A]/60">
                      Découvrir
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* DESKTOP — editorial split rows (image / centered content, alternating) */}
            <div className="hidden md:flex flex-col gap-20 lg:gap-24" data-testid="hebergement-list-desktop">
              {ROOMS.map((r, i) => (
                <div key={r.slug} className="grid grid-cols-12 gap-10 lg:gap-14 items-center">
                  <div className={`col-span-6 ${i % 2 === 1 ? "order-2" : ""}`}>
                    <div className="aspect-[16/11] overflow-hidden">
                      <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className={`col-span-6 flex flex-col items-center text-center ${i % 2 === 1 ? "order-1" : ""}`}>
                    <div className="text-[0.6rem] tracking-[0.5em] uppercase text-[#B8922A] mb-6">
                      · {r.tagline} ·
                    </div>
                    <h3 className="font-serif font-light text-5xl lg:text-6xl leading-[1.02] mb-7 text-[#0A0A0A] max-w-md">
                      {r.name}
                    </h3>
                    <p className="text-base text-[#0A0A0A]/70 font-light leading-[1.85] mb-9 max-w-md">
                      {r.short}
                    </p>
                    <Link
                      to={`/hebergement/${r.slug}${params}`}
                      onClick={() => trackEvent("hebergement_discover", { slug: r.slug })}
                      className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.4em] uppercase text-[#0A0A0A] pb-1.5 relative after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:h-px after:w-10 after:bg-[#0A0A0A]/70 after:transition-all after:duration-500 hover:after:w-full hover:after:bg-[#B8922A] hover:text-[#B8922A]"
                      data-testid={`hebergement-discover-${r.slug}`}
                    >
                      Découvrir
                      <ArrowRight size={14} strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function CounterField({ label, value, onChange, min = 0, max = 20, testId }) {
  return (
    <div>
      <label className="text-[0.6rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 block mb-2">
        <Users size={11} strokeWidth={1.5} className="inline -mt-0.5 mr-1.5" />
        {label}
      </label>
      <div className="flex items-center justify-between gap-2 border border-[#0A0A0A]/15 px-2 py-1.5">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-9 h-9 hover:bg-[#0A0A0A]/5 text-lg leading-none text-[#0A0A0A]/70 transition-colors"
          data-testid={`${testId}-minus`}
          aria-label={`${label} -1`}
        >
          −
        </button>
        <div className="text-lg font-serif font-light text-[#0A0A0A]" data-testid={`${testId}-value`}>
          {value}
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-9 h-9 hover:bg-[#0A0A0A]/5 text-lg leading-none text-[#0A0A0A]/70 transition-colors"
          data-testid={`${testId}-plus`}
          aria-label={`${label} +1`}
        >
          +
        </button>
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, minDate, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="text-[0.6rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 block mb-2">
        <CalendarDays size={11} strokeWidth={1.5} className="inline -mt-0.5 mr-1.5" />
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full text-left border border-[#0A0A0A]/15 hover:border-[#0A0A0A] px-4 py-3.5 text-sm bg-white transition-colors"
            data-testid={testId}
          >
            {value ? (
              <span className="text-[#0A0A0A] capitalize">
                {format(value, "EEE d MMM yyyy", { locale: frLocale })}
              </span>
            ) : (
              <span className="text-[#0A0A0A]/40">Sélectionner</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 bg-white">
          <Calendar
            mode="single"
            selected={value}
            onSelect={(d) => { onChange(d); setOpen(false); }}
            disabled={(d) => (minDate ? d < minDate : false)}
            locale={frLocale}
            classNames={{
              day_selected: "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
